import { useMemo } from "react";
import {
  markChapterNotificationAsRead,
  markAllNotificationsAsRead,
  markAllProjectNotificationsAsRead
} from "./services";
import { useNotificationStore } from "./store";

type TUseNotificationsParams = {
  projectId?: number;
};

export function useNotifications({ projectId }: TUseNotificationsParams) {
  const notifications = useNotificationStore((state) => state.notifications);
  const setNotifications = useNotificationStore((state) => state.setNotifications);

  const filteredNotifications = useMemo(() => {
    if (!projectId) {
      return notifications;
    }
    return notifications;
  }, [notifications, projectId]);

  const markAsRead = async (notificationId: number) => {
    const notification = notifications.find((n) => n.id === notificationId);
    if (!notification) return;

    try {
      await markChapterNotificationAsRead(notification.projectChapterId);

      setNotifications(notifications.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n)));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();

      setNotifications(notifications.map((n) => ({ ...n, isRead: true })));
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  };

  const markAllProjectAsRead = async (projectId: number) => {
    try {
      await markAllProjectNotificationsAsRead(projectId);

      setNotifications(
        notifications.map((n) => (n.projectChapter.projectId === projectId ? { ...n, isRead: true } : n))
      );
    } catch (error) {
      console.error("Failed to mark all project notifications as read:", error);
    }
  };

  const hasUnreadNotifications = useMemo(() => {
    return filteredNotifications.some((n) => !n.isRead);
  }, [filteredNotifications]);

  return {
    notifications: filteredNotifications,
    isLoading: false,
    error: null,
    markAsRead,
    markAllAsRead,
    markAllProjectAsRead,
    hasUnreadNotifications
  };
}
